<template>
  <div
    v-if="isView"
    id="guides"
    v-view-tracker="analyticsParams"
    class="container"
  >
    <div
      class="title obs-el"
      data-attr="guides"
    >
      Гиды рассказывают
    </div>
    <div class="guides-messages">
      <GuideShare
        v-if="guideData"
        :guide-info="guideData"
        :sight="sight"
      />
      <GuideMessage
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        @guide-click="guideClick"
      />
    </div>
    <UiButton
      v-if="isViewMore"
      class="view-more-guide-comments-button"
      :variant="UiButtonVariant.OUTLINE"
      :color-mode="UiButtonColorMode.NEUTRAL"
      text="Показать ещё"
      :is-full-width="isMobile"
      :is-loading="sightsStore.navIsLoading"
      @clicked="showMore"
    />
  </div>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { computed, onMounted, ref } from 'vue';

  import { trackEvent, useViewTracker } from '@/services/analytics';
  import { getGuidesComments, type GuidesComments, getGuideInfo } from '@/shared/api/generated/api';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import { useSightsStore } from '@/shared/store/sights';
  import GuideMessage from '@/shared/ui/GuideMessage.vue';
  import GuideShare from '@/shared/ui/GuideShare.vue';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const sightsStore = useSightsStore();

  const showMore = () => {
    void trackEvent('attractions:sights:sight-popup:guide-show-more.click', analyticsParams.value);
    void fetchData();
  };

  const props = defineProps<{
    sight: CityTagsAdvanced;
  }>();

  const analyticsParams = useSightAnalyticsParams(() => props.sight);

  const vViewTracker = useViewTracker('attractions:sights:sight-popup:guides.view');

  const guideClick = (guideId: number) => {
    void trackEvent('attractions:sights:sight-popup:guide.click', {
      ...analyticsParams.value,
      guide_id: guideId,
    });
  };

  onMounted(() => {
    void fetchData();
    void fetchGuideInfo();
  });

  const comments = ref<GuidesComments[]>([]);
  const guideData = ref<GuidesComments | null>(null);
  const isViewMore = ref<boolean | undefined>(false);
  let page = 1;
  const breakpoints = useScreenSize();

  const isView = computed(() => {
    return comments.value.length || guideData.value;
  });

  const fetchGuideInfo = async () => {
    const sightId = props.sight.id;

    if (!sightId) {
      return;
    }

    try {
      const { data } = await getGuideInfo({ path: { sightId } });

      if (!data) {
        return;
      }

      sightsStore.modalNav.guides = true;
      guideData.value = { guide: data };
    } catch {
      /* noop */
    }
  };

  const fetchData = async () => {
    const sightId = props.sight.id;

    if (!sightId) {
      return;
    }

    sightsStore.navIsLoading = true;

    const { data } = await getGuidesComments({ path: { sightId }, query: { page_size: 3, page } });

    page += 1;
    isViewMore.value = !!data?.next;

    if (!data?.results) {
      return;
    }

    sightsStore.modalNav.guides = !!data.results.length;

    sightsStore.navIsLoading = false;

    comments.value.push(...data.results);
  };

  const isMobile = computed(() => {
    return breakpoints.isMobile.value;
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;

  .container {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .title {
    margin-top: var(--alias-space-48);

    @include legacy-typography.base-600-500-extra;
  }

  .guides-messages {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-16);
    margin-top: var(--alias-space-16);
  }

  .view-more-guide-comments-button {
    margin-top: var(--alias-space-24);
  }
</style>
